import { useState } from 'react'
import { api } from '../lib/api'
import { useVeil } from '../lib/useVeil'
import { maskText } from '../lib/security'

/**
 * Onboarding placement helper (v0.10.0). Privacy contract: matches ONLY inside
 * constellations already visible to this user — nothing global is searchable.
 * Picks become admin-approved claims, never instant links.
 */
export default function FindYourPlace() {
  const [open, setOpen] = useState(false)
  const [father, setFather] = useState('')
  const [mother, setMother] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.suggestPlacement>> | null>(null)
  const [msg, setMsg] = useState('')
  const veil = useVeil()

  const search = async () => {
    setBusy(true); setMsg('')
    try { setResult(await api.suggestPlacement({ father_name: father, mother_name: mother })) }
    catch { setMsg('Could not search right now.') }
    finally { setBusy(false) }
  }

  const claim = async (treeId: string, personId?: string, note?: string) => {
    setMsg('')
    try {
      const r = await api.fileClaim({ tree_id: treeId, kind: personId ? 'claim' : 'join', person_id: personId, note })
      setMsg(r.message)
    } catch (e) {
      setMsg(e instanceof Error && e.message.includes('409') ? 'A request is already pending there.' : 'Could not send the request.')
    }
  }

  return (
    <div className="relative card-glass p-5 mb-6 border-violet-500/20 fade-up" data-tour="findplace">
      <button onClick={() => setOpen(o => !o)} className="w-full text-left flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-100">🧭 Find your place in the family</h2>
          <p className="text-xs text-slate-500">Tell us your parents' names — we search only constellations you can already see, and the tree keepers approve the match.</p>
        </div>
        <span className="text-slate-500 text-sm">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2 mb-3">
            <input value={father} onChange={e => setFather(e.target.value)} placeholder="Father's name (paternal line)"
              className="input-dark !py-2 text-sm flex-1 min-w-[180px]" />
            <input value={mother} onChange={e => setMother(e.target.value)} placeholder="Mother's name (maternal line)"
              className="input-dark !py-2 text-sm flex-1 min-w-[180px]" />
            <button onClick={search} disabled={busy || (!father.trim() && !mother.trim())} className="btn-gold !py-2 text-sm">
              {busy ? 'Searching…' : 'Find matches'}
            </button>
          </div>
          {result && (
            <div className="space-y-3">
              {result.trees.map(t => (
                <div key={t.tree_id} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
                  <p className="text-sm text-slate-200 mb-1.5">🌳 {t.tree_name}</p>
                  {t.self_matches.length > 0 && (
                    <div className="mb-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">could this be you?</p>
                      <div className="flex flex-wrap gap-1.5">
                        {t.self_matches.map(m => (
                          <button key={m.id} onClick={() => claim(t.tree_id, m.id)}
                            className="text-xs px-2.5 py-1 rounded-lg border border-gold-500/30 text-gold-300 hover:bg-gold-500/10">
                            ✋ {maskText(m.name, veil)}{m.birth_year ? ` (b. ${m.birth_year})` : ''} — this is me
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {t.parent_matches.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">parent matches</p>
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {t.parent_matches.map(m => (
                          <span key={`${m.role}-${m.id}`} className="text-xs px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300">
                            {m.role === 'father' ? '👨' : '👩'} {maskText(m.name, veil)}{m.birth_year ? ` (b. ${m.birth_year})` : ''}
                          </span>
                        ))}
                        <button onClick={() => claim(t.tree_id, undefined, `Parent match: ${[father && `father ${father}`, mother && `mother ${mother}`].filter(Boolean).join(', ')}`)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                          Request my place here →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {result.trees.length === 0 && <p className="text-xs text-slate-500">{result.message}</p>}
            </div>
          )}
          {msg && <p className="text-xs text-gold-300 mt-2">{msg}</p>}
        </div>
      )}
    </div>
  )
}
